
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Question, MethodEntity, KnowledgeEntity } from '../types';
import { getCategories, getSubCategories, getTagsForSubCategory } from '../services/categoryService';
import { getQuestions, saveQuestion, hydrateQuestion, getMethods, getKnowledge, saveMethod, saveKnowledge } from '../services/storageService';
import { analyzeQuestionImage } from '../services/geminiService';
import RichTextEditor from '../components/RichTextEditor';
import { ArrowLeft, Loader2, Save, Sparkles, FileImage, AlignLeft, Calendar, Tag, Target, PenTool, Type, Plus, X, Image as ImageIcon, CheckCircle2, AlertCircle, Clipboard, Hash, BookMarked, Layers, Check, RefreshCw, Radio, BookOpen, Link as LinkIcon, Lightbulb, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { getLocalDateTimeString } from '../lib/utils';

const QuestionEntry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [aiStatus, setAiStatus] = useState<'idle' | 'analyzing' | 'success' | 'error'>('idle');
  const [aiErrorMessage, setAiErrorMessage] = useState('');
  const [tempAnalysisImage, setTempAnalysisImage] = useState<string | null>(null);
  const [lastFile, setLastFile] = useState<File | null>(null);
  
  // Metadata Data
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [allMethods, setAllMethods] = useState<MethodEntity[]>([]);
  const [allKnowledge, setAllKnowledge] = useState<KnowledgeEntity[]>([]);

  // Picker States
  const [showMethodPicker, setShowMethodPicker] = useState(false);
  const [showKnowledgePicker, setShowKnowledgePicker] = useState(false);
  const [methodSearch, setMethodSearch] = useState('');
  const [knowledgeSearch, setKnowledgeSearch] = useState('');
  
  // Inline Create States
  const [isCreatingMethod, setIsCreatingMethod] = useState(false);
  const [newMethodTitle, setNewMethodTitle] = useState('');
  const [newMethodContent, setNewMethodContent] = useState('');
  
  const [isCreatingKnowledge, setIsCreatingKnowledge] = useState(false);
  const [newKnowledgeTitle, setNewKnowledgeTitle] = useState('');
  const [newKnowledgeContent, setNewKnowledgeContent] = useState('');
  const [newKnowledgeType, setNewKnowledgeType] = useState<'IDIOM' | 'COMMON_SENSE' | 'CURRENT_AFFAIRS'>('IDIOM');

  // UI States
  const [showSavedFeedback, setShowSavedFeedback] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const lastImportIdRef = useRef<number | null>(null);

  const [formData, setFormData] = useState<Partial<Question>>({
    category: getCategories()[0], 
    subCategory: '',
    tags: [],
    options: ['', '', '', ''],
    accuracy: 60,
    stem: '',
    materialText: '',
    materials: [],
    correctAnswer: 0,
    noteText: '',
    analysis: '', 
    notesImage: '',
    createdAt: Date.now(),
    mistakeCount: 0,
    correctCount: 0,
    methodIds: [],
    knowledgeIds: []
  });

  const [dateString, setDateString] = useState(getLocalDateTimeString());

  // Update available tags when subCategory changes
  useEffect(() => {
      if (formData.subCategory) {
          setAvailableTags(getTagsForSubCategory(formData.subCategory));
      } else {
          setAvailableTags([]);
      }
  }, [formData.subCategory]);

  // Sync Knowledge Type with Category
  useEffect(() => {
      if (formData.category === '常识判断') setNewKnowledgeType('COMMON_SENSE');
      else if (formData.category === '政治理论') setNewKnowledgeType('CURRENT_AFFAIRS');
      else if (formData.category === '言语理解与表达') setNewKnowledgeType('IDIOM');
  }, [formData.category]);

  useEffect(() => {
      const loadMeta = async () => {
          const [m, k] = await Promise.all([getMethods(), getKnowledge()]);
          setAllMethods(m);
          setAllKnowledge(k);
      };
      loadMeta();
  }, []);

  // Listener for External Import
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { type, payload } = event.data || {};
      if (type === 'FENBI_IMPORT') {
        if (payload.importId && payload.importId === lastImportIdRef.current) return;
        if (payload.importId) lastImportIdRef.current = payload.importId;

        setFormData(prev => {
          const newMaterials = [...(prev.materials || [])];
          const htmlContent = payload.materialText || prev.materialText || '';
          if (payload.materials && Array.isArray(payload.materials)) {
             payload.materials.forEach((m: string) => {
                 if (!newMaterials.includes(m) && !htmlContent.includes(m)) newMaterials.push(m);
             });
          }
          return {
            ...prev,
            stem: payload.stem || prev.stem,
            options: (payload.options && payload.options.length === 4) ? payload.options : prev.options,
            correctAnswer: payload.correctAnswer !== undefined ? payload.correctAnswer : prev.correctAnswer,
            noteText: payload.noteText || prev.noteText, 
            analysis: payload.analysis || prev.analysis,
            category: payload.category || prev.category,
            subCategory: payload.subCategory || prev.subCategory,
            tags: payload.tags ? [...new Set([...(prev.tags || []), ...payload.tags])] : prev.tags,
            materials: newMaterials,
            materialText: payload.materialText ? (prev.materialText + (prev.materialText ? '<br/>' : '') + payload.materialText) : prev.materialText,
            accuracy: payload.accuracy || prev.accuracy,
          };
        });
        setTimeout(() => alert("✅ 题目导入成功！"), 100);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    const load = async () => {
        const allQuestions = await getQuestions();
        if (id) {
            setLoadingImages(true);
            try {
                let q = allQuestions.find(q => q.id === id);
                if (q) {
                    if (q.materials.includes('__IMAGE_REF__') || q.notesImage === '__IMAGE_REF__') q = await hydrateQuestion(q);
                    setFormData(q);
                    setDateString(getLocalDateTimeString(q.createdAt));
                }
            } finally { setLoadingImages(false); }
        } else if (location.state?.preserved) {
             const p = location.state.preserved;
             setFormData(prev => ({ ...prev, category: p.category || prev.category, subCategory: p.subCategory || prev.subCategory, tags: p.tags || prev.tags, accuracy: p.accuracy || prev.accuracy, createdAt: Date.now() }));
             setDateString(getLocalDateTimeString());
        }
    };
    load();
  }, [id, location.state]);

  const processAnalysisImage = (file: File) => {
    setLastFile(file);
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target?.result as string;
      setTempAnalysisImage(base64);
      setAiStatus('analyzing');
      setAiErrorMessage('');
      await new Promise(resolve => setTimeout(resolve, 100));
      try {
        const base64Data = base64.split(',')[1];
        const result = await analyzeQuestionImage(base64Data, file.type);
        setFormData(prev => ({
            ...prev, stem: result.stem || prev.stem, materialText: result.materialText || prev.materialText,
            options: result.options && result.options.length === 4 ? result.options : prev.options,
            category: result.category || prev.category,
            subCategory: result.subCategory || prev.subCategory, tags: result.tags || prev.tags,
            accuracy: result.accuracySuggestion || prev.accuracy, correctAnswer: result.answerIndex >= 0 ? result.answerIndex : prev.correctAnswer
        }));
        setAiStatus('success');
      } catch (err: any) { setAiStatus('error'); setAiErrorMessage(err.message || "解析失败"); }
    };
    reader.readAsDataURL(file);
  };

  const handleMaterialUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
        const base64 = event.target?.result as string;
        setFormData(prev => ({ ...prev, materials: [...(prev.materials || []), base64] }));
    };
    reader.readAsDataURL(file);
    e.target.value = ''; 
  };

  const removeMaterial = (index: number) => {
      setFormData(prev => ({ ...prev, materials: (prev.materials || []).filter((_, i) => i !== index) }));
  };

  const handleSave = async (redirect: boolean = true) => {
    if (!formData.stem?.trim()) { alert("请填写题干内容"); return; }
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 100));
    try {
        const newQuestion: Question = {
            id: (redirect && id) ? id : Date.now().toString(), createdAt: formData.createdAt || Date.now(),
            materials: formData.materials || [], materialText: formData.materialText || '', stem: formData.stem || '',
            options: formData.options || [], correctAnswer: formData.correctAnswer || 0, accuracy: formData.accuracy || 60,
            category: formData.category || '常识判断', subCategory: formData.subCategory || '',
            tags: formData.tags || [], mistakeCount: formData.mistakeCount || 0, correctCount: formData.correctCount || 0,
            noteText: formData.noteText || '', analysis: formData.analysis || '', notesImage: formData.notesImage || '',
            methodIds: formData.methodIds || [], knowledgeIds: formData.knowledgeIds || []
        };
        await saveQuestion(newQuestion);
        if (redirect) {
            if (location.state?.returnTo) {
                navigate(location.state.returnTo, { state: { restoreState: location.state.restoreState } });
            } else {
                navigate('/questions');
            }
        } else {
            if (id) {
                navigate('/add', { replace: true, state: { preserved: { category: formData.category, subCategory: formData.subCategory, tags: formData.tags, accuracy: formData.accuracy } } });
            } else {
                setFormData(prev => ({ ...prev, stem: '', options: ['', '', '', ''], materials: [], materialText: '', correctAnswer: 0, noteText: '', analysis: '', mistakeCount: 0, correctCount: 0, notesImage: '', methodIds: [], knowledgeIds: [], createdAt: Date.now() }));
                setTempAnalysisImage(null); setAiStatus('idle'); setAiErrorMessage(''); setDateString(getLocalDateTimeString()); setShowSavedFeedback(true); setTimeout(() => setShowSavedFeedback(false), 2000); window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    } catch (e) { alert("保存失败"); } finally { setLoading(false); }
  };

  // Safe Back Navigation
  const handleBack = () => {
      if (location.state?.returnTo) {
          navigate(location.state.returnTo, { state: { restoreState: location.state.restoreState } });
      } else {
          navigate(-1);
      }
  };

  // --- Association Logic ---
  const toggleMethod = (mId: string) => {
      const current = formData.methodIds || [];
      if (current.includes(mId)) setFormData({ ...formData, methodIds: current.filter(id => id !== mId) });
      else setFormData({ ...formData, methodIds: [...current, mId] });
  };
  
  const toggleKnowledge = (kId: string) => {
      const current = formData.knowledgeIds || [];
      if (current.includes(kId)) setFormData({ ...formData, knowledgeIds: current.filter(id => id !== kId) });
      else setFormData({ ...formData, knowledgeIds: [...current, kId] });
  };

  const handleCreateMethod = async () => {
      if (!newMethodTitle.trim()) return;
      const m: MethodEntity = {
          id: Date.now().toString(), userId: '',
          title: newMethodTitle, content: newMethodContent,
          category: formData.category, subCategory: formData.subCategory, tag: formData.tags?.[0], // Auto-mount to first tag or category
          createdAt: Date.now(), updatedAt: Date.now()
      };
      await saveMethod(m);
      setAllMethods(prev => [m, ...prev]);
      toggleMethod(m.id); // Auto select
      setIsCreatingMethod(false); setNewMethodTitle(''); setNewMethodContent('');
  };

  const handleCreateKnowledge = async () => {
      if (!newKnowledgeTitle.trim()) return;
      const k: KnowledgeEntity = {
          id: Date.now().toString(), 
          title: newKnowledgeTitle, 
          content: newKnowledgeContent, 
          type: newKnowledgeType, 
          createdAt: Date.now()
      };
      await saveKnowledge(k);
      setAllKnowledge(prev => [k, ...prev]);
      toggleKnowledge(k.id); // Auto select
      setIsCreatingKnowledge(false); setNewKnowledgeTitle(''); setNewKnowledgeContent('');
  };

  const addTag = () => { if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) { setFormData(prev => ({ ...prev, tags: [...(prev.tags || []), tagInput.trim()] })); setTagInput(''); } };
  const removeTag = (tag: string) => { setFormData(prev => ({ ...prev, tags: (prev.tags || []).filter(t => t !== tag) })); };

  if (loadingImages) return <div className="flex justify-center p-20"><Loader2 className="animate-spin text-blue-500" /></div>;

  const categories = getCategories();
  const subCategories = getSubCategories(formData.category || categories[0]);

  // Filtering and Sorting for Pickers
  const filteredMethods = useMemo(() => {
      let list = allMethods.filter(m => {
          if (!methodSearch) return true;
          const searchLower = methodSearch.toLowerCase();
          return m.title.toLowerCase().includes(searchLower) || m.tag?.toLowerCase().includes(searchLower);
      });

      // Sort by Relevance (Tag > SubCategory > Category)
      return list.sort((a, b) => {
          const getScore = (m: MethodEntity) => {
              // Highest priority: Exact tag match
              if (m.tag && formData.tags?.includes(m.tag)) return 3;
              // Second priority: SubCategory match
              if (m.subCategory && m.subCategory === formData.subCategory) return 2;
              // Third priority: Category match
              if (m.category && m.category === formData.category) return 1;
              return 0;
          };

          const scoreA = getScore(a);
          const scoreB = getScore(b);

          if (scoreA !== scoreB) return scoreB - scoreA;
          return b.updatedAt - a.updatedAt; // Newest first for ties
      });
  }, [allMethods, methodSearch, formData.category, formData.subCategory, formData.tags]);

  // Filter Knowledge based on strict Category rules
  const filteredKnowledge = allKnowledge.filter(k => {
      // 1. Strict Category Matching
      if (k.type === 'COMMON_SENSE' && formData.category !== '常识判断') return false;
      if (k.type === 'CURRENT_AFFAIRS' && formData.category !== '政治理论') return false;
      if (k.type === 'IDIOM' && formData.category !== '言语理解与表达') return false;

      // 2. Search
      if (!knowledgeSearch) return true;
      return (k.title || '').toLowerCase().includes(knowledgeSearch.toLowerCase()) || k.content.toLowerCase().includes(knowledgeSearch.toLowerCase());
  });

  // Check if knowledge association is available for current category
  const isKnowledgeSupported = ['常识判断', '政治理论', '言语理解与表达'].includes(formData.category || '');
  const knowledgeTypeLabel = formData.category === '常识判断' ? '常识积累' : formData.category === '政治理论' ? '时政积累' : '成语积累';

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20">
       <div className="flex items-center justify-between sticky top-0 bg-gray-50 z-20 py-4 border-b border-gray-200 gap-2">
        <button onClick={handleBack} className="flex items-center text-slate-500 hover:text-slate-800 flex-shrink-0"><ArrowLeft className="mr-1 sm:mr-2" size={20} /> <span className="hidden sm:inline">返回</span></button>
        <h1 className="text-lg sm:text-xl font-bold text-slate-800 truncate">{id ? '编辑错题' : '录入新错题'}</h1>
        <div className="flex gap-2">
            {!id && <button onClick={() => handleSave(false)} disabled={loading} className={`border border-blue-200 px-3 sm:px-4 py-2 rounded-xl flex items-center shadow-sm transition-all disabled:opacity-50 text-sm font-bold ${showSavedFeedback ? 'bg-green-600 text-white border-green-600' : 'bg-white text-blue-600 hover:bg-blue-50'}`}>{loading ? <Loader2 className="animate-spin sm:mr-2" size={16} /> : showSavedFeedback ? <Check size={16} className="sm:mr-2" /> : <Layers size={16} className="sm:mr-2" />}<span className="hidden sm:inline">{showSavedFeedback ? '已保存！' : '保存并录下一题'}</span></button>}
            <button onClick={() => handleSave(true)} disabled={loading} className="bg-blue-600 text-white px-3 sm:px-6 py-2 rounded-xl flex items-center shadow-lg hover:bg-blue-700 transition-all disabled:opacity-50 text-sm font-bold">{loading ? <Loader2 className="animate-spin sm:mr-2" size={18} /> : <Save size={18} className="sm:mr-2" />}<span className="hidden sm:inline">保存</span></button>
        </div>
      </div>

      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-slate-700 flex items-center"><span className="flex items-center text-blue-600"><Sparkles size={18} className="mr-2" /> 智能识别区</span></h3></div>
        <div className={`border-2 border-dashed rounded-xl min-h-[160px] flex flex-col items-center justify-center transition-colors relative cursor-pointer overflow-hidden group ${aiStatus === 'error' ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50/50 hover:bg-blue-50'}`} onPaste={(e) => { const items = e.clipboardData.items; for (let i = 0; i < items.length; i++) { if (items[i].type.indexOf('image') !== -1) { e.preventDefault(); const file = items[i].getAsFile(); if(file) processAnalysisImage(file); } } }}>
            {tempAnalysisImage && aiStatus !== 'analyzing' ? (
                <div className="relative w-full h-auto min-h-[200px] flex flex-col justify-center items-center bg-gray-100 p-4"><img src={tempAnalysisImage} className="max-h-64 object-contain transition-opacity opacity-60" /><div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">{aiStatus === 'success' && <span className="bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-bold shadow-md flex items-center"><CheckCircle2 size={16} className="mr-2"/> 识别成功</span>}</div></div>
            ) : aiStatus === 'analyzing' ? (<div className="flex flex-col items-center text-blue-600"><Loader2 className="animate-spin mb-2" size={32} /><span className="font-bold">AI 正在深度分析...</span></div>) : (<><FileImage size={48} className="text-blue-200 mb-2 group-hover:scale-110 transition-transform"/><p className="font-medium text-blue-400">点击、拖拽或粘贴图片</p></>)}
            <input type="file" onChange={(e) => e.target.files?.[0] && processAnalysisImage(e.target.files[0])} className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" />
        </div>
      </div>

      <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div><label className="block text-sm font-medium text-slate-700 mb-1">大类</label><select className="w-full border-gray-200 rounded-lg py-2.5 bg-gray-50" value={formData.category} onChange={(e) => { const newCat = e.target.value; setFormData({...formData, category: newCat, subCategory: getSubCategories(newCat)[0]}); }}>{categories.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
             <div><label className="block text-sm font-medium text-slate-700 mb-1">小类</label><select className="w-full border-gray-200 rounded-lg py-2.5 bg-gray-50" value={formData.subCategory} onChange={(e) => setFormData({...formData, subCategory: e.target.value})}><option value="">请选择...</option>{subCategories.map(sc => <option key={sc} value={sc}>{sc}</option>)}</select></div>
             <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center"><Tag size={14} className="mr-1" /> 考点标签</label>
                <div className="flex flex-wrap gap-2 mb-2">{formData.tags?.map(tag => (<span key={tag} className="bg-blue-50 text-blue-600 px-2 py-1 rounded text-sm flex items-center">{tag}<button onClick={() => removeTag(tag)} className="ml-1 hover:text-red-500"><X size={12}/></button></span>))}</div>
                <div className="flex gap-2">
                    <input list="tag-suggestions" type="text" className="flex-1 border-gray-200 rounded-lg py-2 px-3" placeholder="输入考点..." value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addTag()} />
                    <datalist id="tag-suggestions">{availableTags.map(t => <option key={t} value={t} />)}</datalist>
                    <button onClick={addTag} className="bg-gray-100 text-slate-600 px-3 py-2 rounded-lg hover:bg-gray-200"><Plus size={18} /></button>
                </div>
             </div>
             <div><label className="block text-sm font-medium text-slate-700 mb-1">录入时间</label><input type="datetime-local" className="w-full border-gray-200 rounded-lg py-2 px-3 text-sm" value={dateString} onChange={(e) => { setDateString(e.target.value); const date = new Date(e.target.value); if (!isNaN(date.getTime())) setFormData({...formData, createdAt: date.getTime()}); }} /></div>
             <div><label className="block text-sm font-medium text-slate-700 mb-1">正确率 (%)</label><input type="number" min="0" max="100" className="w-full border-gray-200 rounded-lg py-2 px-3" value={formData.accuracy} onChange={(e) => setFormData({...formData, accuracy: parseInt(e.target.value) || 0})} /></div>
          </div>
          <hr className="border-gray-100" />
          
          {/* Methodology & Knowledge Association Section */}
          <div className="space-y-4">
              <h4 className="font-bold text-slate-700 flex items-center"><LinkIcon size={18} className="mr-2" /> 知识体系关联</h4>
              
              {/* Method Links */}
              <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                  <div className="flex justify-between items-center mb-2">
                      <label className="text-sm font-bold text-blue-800 flex items-center"><Lightbulb size={14} className="mr-1.5"/> 关联方法论</label>
                      <button onClick={() => setShowMethodPicker(!showMethodPicker)} className="text-xs bg-white text-blue-600 px-2 py-1 rounded border border-blue-200 hover:bg-blue-50 font-medium">
                          {showMethodPicker ? '收起' : '添加关联'}
                      </button>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-2">
                      {(formData.methodIds || []).map(mid => {
                          const m = allMethods.find(item => item.id === mid);
                          if (!m) return null;
                          return (
                              <div key={mid} className="bg-white text-slate-700 px-3 py-1.5 rounded-lg border border-blue-200 text-sm flex items-center shadow-sm">
                                  {m.title}
                                  <button onClick={() => toggleMethod(mid)} className="ml-2 text-slate-400 hover:text-red-500"><X size={14}/></button>
                              </div>
                          );
                      })}
                      {(formData.methodIds || []).length === 0 && <span className="text-xs text-slate-400 italic">暂无关联方法</span>}
                  </div>

                  {showMethodPicker && (
                      <div className="bg-white rounded-xl border border-blue-100 p-3 mt-2 shadow-sm animate-in fade-in slide-in-from-top-2">
                          {!isCreatingMethod ? (
                              <>
                                  <div className="flex gap-2 mb-2">
                                      <input type="text" placeholder="搜索方法..." className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5" value={methodSearch} onChange={e => setMethodSearch(e.target.value)} />
                                      <button onClick={() => setIsCreatingMethod(true)} className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap"><Plus size={14} className="inline mr-1"/>新建</button>
                                  </div>
                                  <div className="max-h-40 overflow-y-auto space-y-1 custom-scrollbar">
                                      {filteredMethods.map(m => {
                                          // Determine match type for badge
                                          let matchType = '';
                                          if (m.tag && formData.tags?.includes(m.tag)) matchType = '考点';
                                          else if (m.subCategory && m.subCategory === formData.subCategory) matchType = '子类';
                                          else if (m.category && m.category === formData.category) matchType = '大类';

                                          return (
                                              <div key={m.id} onClick={() => toggleMethod(m.id)} className={`p-2 rounded-lg text-sm cursor-pointer flex justify-between items-center ${formData.methodIds?.includes(m.id) ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}>
                                                  <div className="flex items-center truncate">
                                                      {matchType && <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 rounded mr-2 flex-shrink-0">{matchType}</span>}
                                                      <span className="truncate">{m.title}</span>
                                                  </div>
                                                  {formData.methodIds?.includes(m.id) && <Check size={14} className="flex-shrink-0 ml-2"/>}
                                              </div>
                                          );
                                      })}
                                      {filteredMethods.length === 0 && <div className="text-center text-xs text-slate-400 py-2">未找到匹配方法</div>}
                                  </div>
                              </>
                          ) : (
                              <div className="space-y-2">
                                  <input type="text" placeholder="方法标题" className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 font-bold" value={newMethodTitle} onChange={e => setNewMethodTitle(e.target.value)} />
                                  <textarea placeholder="方法内容简述..." className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 h-20 resize-none" value={newMethodContent} onChange={e => setNewMethodContent(e.target.value)} />
                                  <div className="flex justify-end gap-2">
                                      <button onClick={() => setIsCreatingMethod(false)} className="text-xs text-slate-500 px-2 py-1">取消</button>
                                      <button onClick={handleCreateMethod} className="bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-bold">创建并关联</button>
                                  </div>
                              </div>
                          )}
                      </div>
                  )}
              </div>

              {/* Knowledge Links - Only show if supported for current category or if already has links */}
              {(isKnowledgeSupported || (formData.knowledgeIds && formData.knowledgeIds.length > 0)) && (
                  <div className="bg-amber-50/50 p-4 rounded-xl border border-amber-100">
                      <div className="flex justify-between items-center mb-2">
                          <label className="text-sm font-bold text-amber-800 flex items-center">
                              <BookOpen size={14} className="mr-1.5"/> 碎片知识点
                              {isKnowledgeSupported && <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 rounded">{knowledgeTypeLabel}</span>}
                          </label>
                          {isKnowledgeSupported && (
                              <button onClick={() => setShowKnowledgePicker(!showKnowledgePicker)} className="text-xs bg-white text-amber-600 px-2 py-1 rounded border border-amber-200 hover:bg-amber-50 font-medium">
                                  {showKnowledgePicker ? '收起' : '添加关联'}
                              </button>
                          )}
                      </div>
                      <div className="flex flex-wrap gap-2 mb-2">
                          {(formData.knowledgeIds || []).map(kid => {
                              const k = allKnowledge.find(item => item.id === kid);
                              if (!k) return null;
                              return (
                                  <div key={kid} className="bg-white text-slate-700 px-3 py-1.5 rounded-lg border border-amber-200 text-sm flex items-center shadow-sm">
                                      {k.title || k.content.slice(0, 15)}
                                      <button onClick={() => toggleKnowledge(kid)} className="ml-2 text-slate-400 hover:text-red-500"><X size={14}/></button>
                                  </div>
                              );
                          })}
                          {(formData.knowledgeIds || []).length === 0 && <span className="text-xs text-slate-400 italic">暂无关联知识点</span>}
                      </div>

                      {showKnowledgePicker && isKnowledgeSupported && (
                          <div className="bg-white rounded-xl border border-amber-100 p-3 mt-2 shadow-sm animate-in fade-in slide-in-from-top-2">
                              {!isCreatingKnowledge ? (
                                  <>
                                      <div className="flex gap-2 mb-2">
                                          <input type="text" placeholder={`搜索${knowledgeTypeLabel}...`} className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5" value={knowledgeSearch} onChange={e => setKnowledgeSearch(e.target.value)} />
                                          <button onClick={() => setIsCreatingKnowledge(true)} className="bg-amber-500 text-white px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap"><Plus size={14} className="inline mr-1"/>新建</button>
                                      </div>
                                      <div className="max-h-40 overflow-y-auto space-y-1 custom-scrollbar">
                                          {filteredKnowledge.map(k => (
                                              <div key={k.id} onClick={() => toggleKnowledge(k.id)} className={`p-2 rounded-lg text-sm cursor-pointer flex justify-between items-center ${formData.knowledgeIds?.includes(k.id) ? 'bg-amber-50 text-amber-700' : 'hover:bg-gray-50'}`}>
                                                  <span className="truncate mr-2">{k.title || k.content.replace(/<[^>]+>/g, '').slice(0, 20)}</span>
                                                  {formData.knowledgeIds?.includes(k.id) && <Check size={14}/>}
                                              </div>
                                          ))}
                                          {filteredKnowledge.length === 0 && <div className="text-center text-xs text-slate-400 py-2">未找到匹配{knowledgeTypeLabel}</div>}
                                      </div>
                                  </>
                              ) : (
                                  <div className="space-y-2">
                                      <input type="text" placeholder={`标题 (${knowledgeTypeLabel})`} className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 font-bold" value={newKnowledgeTitle} onChange={e => setNewKnowledgeTitle(e.target.value)} />
                                      <input type="text" placeholder="内容简述..." className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5" value={newKnowledgeContent} onChange={e => setNewKnowledgeContent(e.target.value)} />
                                      <div className="flex justify-between items-center">
                                          <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-100">{knowledgeTypeLabel}</span>
                                          <div className="flex gap-2">
                                              <button onClick={() => setIsCreatingKnowledge(false)} className="text-xs text-slate-500 px-2 py-1">取消</button>
                                              <button onClick={handleCreateKnowledge} className="bg-amber-500 text-white px-3 py-1 rounded-lg text-xs font-bold">创建并关联</button>
                                          </div>
                                      </div>
                                  </div>
                              )}
                          </div>
                      )}
                  </div>
              )}
          </div>
          
          <hr className="border-gray-100" />
          
          {/* Editor Areas */}
          <div><label className="block text-sm font-medium text-slate-700 mb-1">题干内容</label><textarea className="w-full border-gray-200 rounded-lg h-24 focus:ring-blue-500 p-3 text-lg" value={formData.stem} onChange={(e) => setFormData({...formData, stem: e.target.value})} placeholder="题目描述..." /></div>
          <div className="space-y-4"><label className="block text-sm font-medium text-slate-700">选项设置</label>{formData.options?.map((opt, idx) => (<div key={idx} className="flex items-center group"><span className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 font-bold transition-colors ${formData.correctAnswer === idx ? 'bg-green-500 text-white' : 'bg-gray-100 text-slate-500'}`}>{String.fromCharCode(65 + idx)}</span><input type="text" className="flex-1 border-gray-200 rounded-lg focus:ring-blue-500 py-2 px-3 text-lg" value={opt} placeholder={`选项 ${String.fromCharCode(65 + idx)}`} onChange={(e) => { const newOpts = [...(formData.options || [])]; newOpts[idx] = e.target.value; setFormData({...formData, options: newOpts}); }} /><label className="ml-3 flex items-center cursor-pointer text-sm text-slate-500 hover:text-green-600"><input type="radio" name="correctAnswer" className="mr-1 text-green-600" checked={formData.correctAnswer === idx} onChange={() => setFormData({...formData, correctAnswer: idx})} /> 正确答案</label></div>))}</div>
      </div>
      
      {/* Material & Analysis & Notes sections same as before, omitted for brevity but conceptually present */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-100 px-6 py-3 flex items-center gap-4"><h3 className="font-bold text-slate-700 flex items-center"><AlignLeft size={18} className="mr-2"/> 题目材料</h3></div>
          <div className="p-6">
             <div className="grid grid-cols-4 gap-2 mb-4">
                 {formData.materials?.map((m,i) => <div key={i} className="relative group"><img src={m} className="w-full h-24 object-contain border rounded"/><button onClick={() => removeMaterial(i)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100"><X size={12}/></button></div>)}
                 <label className="border-2 border-dashed rounded flex items-center justify-center h-24 cursor-pointer hover:bg-gray-50"><Plus size={24} className="text-gray-300"/><input type="file" className="hidden" accept="image/*" onChange={handleMaterialUpload}/></label>
             </div>
             <RichTextEditor value={formData.materialText || ''} onChange={(html) => setFormData({...formData, materialText: html})} placeholder="输入文字材料..." />
          </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-100 px-6 py-3 flex items-center gap-4"><h3 className="font-bold text-slate-700 flex items-center"><BookOpen size={18} className="mr-2"/> 题目解析</h3></div>
          <div className="p-6"><RichTextEditor value={formData.analysis || ''} onChange={(html) => setFormData({...formData, analysis: html})} placeholder="输入解析..." /></div>
      </div>
      
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-100 px-6 py-3 flex items-center gap-4"><h3 className="font-bold text-slate-700 flex items-center"><BookMarked size={18} className="mr-2"/> 我的笔记</h3></div>
          <div className="p-6"><RichTextEditor value={formData.noteText || ''} onChange={(html) => setFormData({...formData, noteText: html})} placeholder="输入笔记..." /></div>
      </div>
    </div>
  );
};

export default QuestionEntry;
